import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { map, catchError, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { NgZone } from '@angular/core';
import { LoggerService } from '../services/logger.service';

/**
 * Only a 401 means the session is gone: the backend answers every dead session
 * (expired, revoked, missing cookie) with one. Anything else — no network right
 * after the laptop wakes up, the service worker's synthetic 504, a 5xx during a
 * deploy — says nothing about the session and must not log the user out.
 */
const isSessionRejected = (error: unknown) =>
  error instanceof HttpErrorResponse && error.status === 401;

export const authGuard = () => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const ngZone = inject(NgZone);
  const logger = inject(LoggerService);

  const log = (message: string, data?: any) => {
    ngZone.run(() => {
      const timestamp = new Date().toISOString();
      const logMessage = `[AuthGuard] ${timestamp} - ${message}`;
      if (data) {
        logger.warn(logMessage, data);
      } else {
        logger.warn(logMessage);
      }
    });
  };

  return authService.verifyAuth().pipe(
    tap((initialVerifyResult) => {
      log('Initial token verification', { result: initialVerifyResult });
    }),
    switchMap((isValid) => {
      if (!isValid) {
        log('Verification returned false, attempting refresh');
        return authService.refreshToken().pipe(
          tap(() => {
            log('Token refresh successful');
          }),
          switchMap(() => {
            log('Re-verifying with new token');
            return authService.verifyAuth();
          }),
          catchError((refreshError) => {
            log('Token refresh failed', refreshError);
            if (isSessionRejected(refreshError)) return of(false);
            throw refreshError;
          }),
        );
      }
      return of(isValid);
    }),
    catchError((error) => {
      if (isSessionRejected(error)) return of(false);
      // The backend could not be reached, so the session is unknown, not invalid.
      // Fall back to the local session and let the page load; its own requests run
      // through the interceptor, which still logs out once the backend rejects it.
      log('Could not verify session, falling back to the local one', error);
      return of(authService.isLoggedIn());
    }),
    map((isAuthenticated) => {
      log('Final authentication status', { isAuthenticated });

      if (!isAuthenticated) {
        authService.clearSession();
        const currentUrl = router.url;
        log('Access denied, redirecting to login', { attemptedUrl: currentUrl });
        router.navigate(['/login'], {
          queryParams: { redirect: currentUrl },
        });
        return false;
      }

      log('Access granted');
      return true;
    }),
  );
};
